import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PrescriptionsPanel from "@/components/PrescriptionsPanel";
import LabPanel from "@/components/LabPanel";
import { Lock, LockOpen, Plus, MagnifyingGlass, Trash, SealCheck, UserSwitch } from "@phosphor-icons/react";

const VITALS = [["sistolik", "Sistolik"], ["diastolik", "Diastolik"], ["nadi", "Nadi"], ["napas", "Napas"], ["suhu", "Suhu °C"], ["spo2", "SpO₂ %"], ["berat", "Berat kg"], ["tinggi", "Tinggi cm"]];

export default function ClinicalRecord() {
  const { encounterId } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const [d, setD] = useState(null);
  const [icdQ, setIcdQ] = useState("");
  const [icd, setIcd] = useState([]);
  const [signPw, setSignPw] = useState(null);
  const [add, setAdd] = useState({ reason: "", text: "" });
  const [tab, setTab] = useState("klinis");
  const [readiness, setReadiness] = useState(null);
  const [reassign, setReassign] = useState(null); // {staff_id, reason}
  const [clinicStaff, setClinicStaff] = useState([]);
  useEffect(() => { api.get(`/satusehat/encounter/${encounterId}/readiness`).then((r) => setReadiness(r.data)).catch(() => {}); }, [encounterId]);
  const inC = "w-full rounded-lg border border-pink-100 bg-[#FDFBFE] px-2 py-1.5 text-sm";

  const load = useCallback(() => {
    api.get(`/clinical/encounter/${encounterId}`).then((r) => setD(r.data)).catch((e) => toast.error(apiError(e)));
  }, [encounterId]);
  useEffect(() => { document.title = "Rekam Medis — HalloGBS"; load(); }, [load]);

  const set = (k, v) => setD({ ...d, [k]: v });
  const setV = (k, v) => setD({ ...d, vital: { ...(d.vital || {}), [k]: v } });
  const setS = (k, v) => setD({ ...d, soap: { ...(d.soap || {}), [k]: v } });

  const searchIcd = async (q) => { setIcdQ(q); if (!q) { setIcd([]); return; } try { setIcd((await api.get(`/clinical/icd10?q=${encodeURIComponent(q)}`)).data.results); } catch { /* ignore */ } };
  const addDx = (x, type) => { if ((d.diagnoses || []).some((y) => y.code === x.code)) return; set("diagnoses", [...(d.diagnoses || []), { ...x, type }]); setIcdQ(""); setIcd([]); };
  const rmDx = (code) => set("diagnoses", (d.diagnoses || []).filter((y) => y.code !== code));

  const save = async () => {
    try { const r = await api.put(`/clinical/encounter/${encounterId}`, { ...d, expected_version: d.version }); setD(r.data); toast.success("Draft tersimpan"); }
    catch (e) { toast.error(apiError(e)); if (e?.response?.status === 409) load(); }
  };
  const doSign = async () => {
    try { const r = await api.post(`/clinical/encounter/${encounterId}/sign`, { password: signPw }); setD(r.data); setSignPw(null); toast.success("Rekam disahkan & terkunci"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const doAdd = async () => {
    try { const r = await api.post(`/clinical/encounter/${encounterId}/addendum`, add); setD(r.data); setAdd({ reason: "", text: "" }); toast.success("Adendum ditambahkan"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const openReassign = async () => {
    try {
      const r = await api.get("/staff");
      setClinicStaff((r.data || []).filter((s) => s.aktif && ["Bidan", "Dokter", "Perawat"].includes(s.jenis)));
    } catch { /* ignore */ }
    setReassign({ staff_id: "", reason: "" });
  };
  const doReassign = async () => {
    if (!reassign.staff_id || !reassign.reason.trim()) return toast.error("Pilih nakes tujuan & isi alasan");
    try {
      await api.post(`/clinical/encounter/${encounterId}/reassign`, reassign);
      setReassign(null); toast.success("Encounter dialihkan ke nakes terpilih"); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!d) return <div className="p-8 text-center text-slate-400">Memuat…</div>;
  const locked = d.status === "signed";
  const ro = locked || !d.can_write;

  return (
    <div className="space-y-4" data-testid="clinical-record">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="font-heading text-xl font-extrabold text-[#4C1D95]">Rekam Medis — {d.patient_nama}</h1>
          <p className="text-xs text-slate-500">{d.service} · {d.lokasi} · {d.practitioner_nama}</p>
        </div>
        <span data-testid="clinical-status" className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${locked ? "bg-emerald-100 text-emerald-700" : d.status === "draft" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"}`}>
          {locked ? <Lock size={13} weight="fill" /> : <LockOpen size={13} />} {locked ? (d.addenda?.length ? "Disahkan · Ada Adendum" : "Disahkan (terkunci)") : d.status === "draft" ? "Draft" : "Baru"}
        </span>
      </div>

      {!d.can_write && (
        <div data-testid="clinical-readonly-note" className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-slate-100 px-3 py-2 text-xs font-bold text-slate-600">
          <span>Mode baca saja — akun Anda bukan tenaga kesehatan klinis yang ditugaskan pada kunjungan ini.</span>
          {isAdmin && !locked && <button data-testid="cl-reassign-btn" onClick={openReassign} className="flex items-center gap-1.5 rounded-lg bg-[#EEE6FF] px-3 py-1.5 text-[11px] font-bold text-[#6A38C7]"><UserSwitch size={13} weight="bold" /> Alihkan Penugasan (Reassign)</button>}
        </div>
      )}
      {d.can_write && isAdmin && !locked && (
        <div className="flex justify-end">
          <button data-testid="cl-reassign-btn" onClick={openReassign} className="flex items-center gap-1.5 rounded-lg bg-[#EEE6FF] px-3 py-1.5 text-[11px] font-bold text-[#6A38C7]"><UserSwitch size={13} weight="bold" /> Alihkan Penugasan (Reassign)</button>
        </div>
      )}

      {readiness && (
        <div data-testid="sync-readiness" className={`rounded-2xl border p-3 ${readiness.ready ? "border-emerald-200 bg-emerald-50" : "border-rose-200 bg-rose-50"}`}>
          <p className={`flex items-center gap-2 text-sm font-extrabold ${readiness.ready ? "text-emerald-700" : "text-rose-700"}`}>
            <span data-testid="sync-dot" className={`inline-block h-3 w-3 rounded-full ${readiness.ready ? "bg-emerald-500" : "bg-rose-500"}`} />
            Kesiapan SATUSEHAT: {readiness.ready ? "Siap disinkronkan" : "Belum siap"}
          </p>
          {!readiness.ready && (
            <ul className="mt-2 flex flex-wrap gap-1.5" data-testid="sync-reasons">
              {readiness.checks.filter((c) => !c.ok).map((c, i) => (
                <li key={i} className="rounded-lg bg-white/70 px-2 py-1 text-[11px] font-semibold text-rose-600">{c.reason}</li>
              ))}
            </ul>
          )}
          <p className="mt-1 text-[11px] text-slate-400">Indikator read-only. Tidak ada pengiriman/sinkronisasi ke SATUSEHAT.</p>
        </div>
      )}

      <div className="flex gap-2">
        <button data-testid="cl-tab-klinis" onClick={() => setTab("klinis")} className={`rounded-xl px-4 py-2 text-sm font-bold ${tab === "klinis" ? "bg-[#7C4DE0] text-white" : "bg-slate-100 text-slate-600"}`}>Catatan Klinis</button>
        <button data-testid="cl-tab-resep" onClick={() => setTab("resep")} className={`rounded-xl px-4 py-2 text-sm font-bold ${tab === "resep" ? "bg-[#7C4DE0] text-white" : "bg-slate-100 text-slate-600"}`}>Resep</button>
        <button data-testid="cl-tab-lab" onClick={() => setTab("lab")} className={`rounded-xl px-4 py-2 text-sm font-bold ${tab === "lab" ? "bg-[#7C4DE0] text-white" : "bg-slate-100 text-slate-600"}`}>Hasil Lab</button>
      </div>

      {tab === "resep" && <PrescriptionsPanel encounterId={encounterId} />}
      {tab === "lab" && <LabPanel encounterId={encounterId} />}

      {tab === "klinis" && (<>
      <div className="grid gap-3 rounded-2xl border border-pink-100 bg-white p-4 sm:grid-cols-2">
        <label className="text-xs font-bold text-slate-600 sm:col-span-2">Keluhan Utama<textarea data-testid="cl-keluhan" disabled={ro} className={inC} rows={2} value={d.keluhan || ""} onChange={(e) => set("keluhan", e.target.value)} /></label>
        <label className="text-xs font-bold text-slate-600">Anamnesis<textarea disabled={ro} className={inC} rows={2} value={d.anamnesis || ""} onChange={(e) => set("anamnesis", e.target.value)} /></label>
        <label className="text-xs font-bold text-slate-600">Riwayat Alergi<textarea disabled={ro} className={inC} rows={2} value={d.alergi || ""} onChange={(e) => set("alergi", e.target.value)} /></label>
      </div>

      <div className="rounded-2xl border border-pink-100 bg-white p-4">
        <div className="mb-2 text-xs font-bold uppercase text-slate-500">Tanda Vital</div>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {VITALS.map(([k, lbl]) => <label key={k} className="text-[11px] font-bold text-slate-600">{lbl}<input data-testid={`cl-vital-${k}`} disabled={ro} className={inC} value={(d.vital || {})[k] || ""} onChange={(e) => setV(k, e.target.value)} /></label>)}
        </div>
      </div>

      <div className="grid gap-3 rounded-2xl border border-pink-100 bg-white p-4 sm:grid-cols-2">
        {[["s", "S — Subjective"], ["o", "O — Objective"], ["a", "A — Assessment"], ["p", "P — Plan"]].map(([k, lbl]) => (
          <label key={k} className="text-xs font-bold text-slate-600">{lbl}<textarea data-testid={`cl-soap-${k}`} disabled={ro} className={inC} rows={2} value={(d.soap || {})[k] || ""} onChange={(e) => setS(k, e.target.value)} /></label>
        ))}
      </div>

      <div className="rounded-2xl border border-pink-100 bg-white p-4">
        <div className="mb-2 text-xs font-bold uppercase text-slate-500">Diagnosis (ICD-10)</div>
        {!ro && (
          <div className="relative mb-2">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input data-testid="cl-icd-search" className={inC + " pl-8"} placeholder="Cari kode/nama ICD-10" value={icdQ} onChange={(e) => searchIcd(e.target.value)} />
            {icd.length > 0 && (
              <div className="absolute z-10 mt-1 max-h-48 w-full overflow-auto rounded-xl border border-pink-100 bg-white shadow">
                {icd.map((x) => (
                  <div key={x.code} className="flex items-center justify-between gap-2 border-b border-pink-50 px-3 py-2 text-sm">
                    <span><b>{x.code}</b> {x.name}</span>
                    <span className="flex gap-1">
                      <button data-testid={`cl-icd-add-${x.code}`} onClick={() => addDx(x, "utama")} className="rounded bg-[#7C4DE0] px-2 py-0.5 text-[11px] font-bold text-white">Utama</button>
                      <button onClick={() => addDx(x, "tambahan")} className="rounded bg-slate-200 px-2 py-0.5 text-[11px] font-bold text-slate-600">Tambahan</button>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        <div className="space-y-1.5">
          {(d.diagnoses || []).map((x) => (
            <div key={x.code} data-testid={`cl-dx-${x.code}`} className="flex items-center justify-between rounded-lg bg-[#FAF7FF] px-3 py-1.5 text-sm">
              <span><span className="rounded bg-white px-1.5 py-0.5 text-[10px] font-bold text-[#7C4DE0]">{x.type}</span> <b>{x.code}</b> {x.name}</span>
              {!ro && <button onClick={() => rmDx(x.code)} className="text-rose-500"><Trash size={14} /></button>}
            </div>
          ))}
          {(d.diagnoses || []).length === 0 && <div className="text-xs text-slate-400">Belum ada diagnosis.</div>}
        </div>
      </div>

      <div className="grid gap-3 rounded-2xl border border-pink-100 bg-white p-4 sm:grid-cols-2">
        <label className="text-xs font-bold text-slate-600">Tindakan<textarea disabled={ro} className={inC} rows={2} value={d.tindakan || ""} onChange={(e) => set("tindakan", e.target.value)} /></label>
        <label className="text-xs font-bold text-slate-600">Edukasi<textarea disabled={ro} className={inC} rows={2} value={d.edukasi || ""} onChange={(e) => set("edukasi", e.target.value)} /></label>
        <label className="text-xs font-bold text-slate-600 sm:col-span-2">Rencana Kontrol / Rujukan<textarea disabled={ro} className={inC} rows={2} value={d.rencana || ""} onChange={(e) => set("rencana", e.target.value)} /></label>
      </div>

      {d.can_write && !locked && (
        <div className="flex flex-wrap gap-3">
          <button data-testid="cl-save-btn" onClick={save} className="rounded-xl bg-[#7C4DE0] px-5 py-2.5 text-sm font-bold text-white">Simpan Draft</button>
          <button data-testid="cl-sign-btn" onClick={() => setSignPw("")} disabled={d.status === "none"} className="flex items-center gap-1.5 rounded-xl bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white disabled:opacity-50"><SealCheck size={16} weight="fill" /> Sahkan (kunci)</button>
        </div>
      )}

      {locked && (
        <div className="rounded-2xl border border-pink-100 bg-white p-4" data-testid="cl-addenda">
          <div className="mb-2 text-xs font-bold uppercase text-slate-500">Adendum ({(d.addenda || []).length})</div>
          {(d.addenda || []).map((a, i) => (
            <div key={i} className="mb-2 rounded-lg bg-[#FAF7FF] px-3 py-2 text-sm">
              <div className="text-[11px] font-bold text-slate-500">{a.by} · {a.profession || a.role} · {a.at ? new Date(a.at).toLocaleString("id-ID") : ""}</div>
              <div className="text-xs font-bold text-[#7C4DE0]">Alasan: {a.reason}</div>
              <div>{a.text}</div>
            </div>
          ))}
          {d.can_write && (
            <div className="mt-2 grid gap-2">
              <input data-testid="cl-add-reason" className={inC} placeholder="Alasan adendum" value={add.reason} onChange={(e) => setAdd({ ...add, reason: e.target.value })} />
              <textarea data-testid="cl-add-text" className={inC} rows={2} placeholder="Isi adendum" value={add.text} onChange={(e) => setAdd({ ...add, text: e.target.value })} />
              <button data-testid="cl-add-btn" onClick={doAdd} className="w-fit rounded-xl bg-indigo-600 px-4 py-2 text-sm font-bold text-white"><Plus size={14} className="mr-1 inline" />Tambah Adendum</button>
            </div>
          )}
        </div>
      )}
      </>)}

      {signPw !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setSignPw(null)}>
          <div data-testid="cl-sign-modal" onClick={(e) => e.stopPropagation()} className="w-full max-w-sm rounded-3xl bg-white p-6">
            <h3 className="font-heading text-lg font-extrabold text-[#4C1D95]">Re-autentikasi</h3>
            <p className="mt-1 text-xs text-slate-500">Masukkan password Anda untuk mengesahkan & mengunci rekam ini.</p>
            <input data-testid="cl-sign-password" type="password" className={inC + " mt-3"} value={signPw} onChange={(e) => setSignPw(e.target.value)} autoFocus />
            <button data-testid="cl-sign-confirm" onClick={doSign} className="mt-4 w-full rounded-xl bg-emerald-600 py-2.5 text-sm font-bold text-white">Sahkan Sekarang</button>
          </div>
        </div>
      )}

      {reassign !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setReassign(null)}>
          <div data-testid="cl-reassign-modal" onClick={(e) => e.stopPropagation()} className="w-full max-w-sm rounded-3xl bg-white p-6">
            <h3 className="font-heading text-lg font-extrabold text-[#4C1D95]">Alihkan Penugasan Encounter</h3>
            <p className="mt-1 text-xs text-slate-500">Pengambilalihan resmi ke tenaga kesehatan klinis aktif lain. Wajib beralasan & tercatat di audit.</p>
            <label className="mt-3 block text-xs font-bold text-slate-600">Nakes Tujuan
              <select data-testid="cl-reassign-staff" className={inC + " mt-1"} value={reassign.staff_id} onChange={(e) => setReassign({ ...reassign, staff_id: e.target.value })}>
                <option value="">— Pilih nakes —</option>
                {clinicStaff.map((s) => <option key={s.id} value={s.id}>{s.nama} ({s.jenis})</option>)}
              </select>
            </label>
            <label className="mt-2 block text-xs font-bold text-slate-600">Alasan
              <textarea data-testid="cl-reassign-reason" className={inC + " mt-1"} rows={2} value={reassign.reason} onChange={(e) => setReassign({ ...reassign, reason: e.target.value })} />
            </label>
            <button data-testid="cl-reassign-confirm" onClick={doReassign} className="mt-4 w-full rounded-xl bg-[#7C4DE0] py-2.5 text-sm font-bold text-white">Alihkan Sekarang</button>
          </div>
        </div>
      )}
    </div>
  );
}
